import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Play, Music, Film, Video as VideoIcon, Sparkles, Headphones, Users } from "lucide-react";
import { api } from "@/lib/api";
import PageTransition, { Section } from "@/components/site/PageTransition";
import Seo from "@/components/site/Seo";
import YouTubeHoverCard from "@/components/site/YouTubeHoverCard";
import { PulseLogo } from "@/components/site/Header";
import LogoAnimation from "@/components/site/LogoAnimation";
import { useVideoPopup } from "@/components/site/VideoPopup";
import Avatar from "@/components/site/Avatar";

const HERO_YOUTUBE_ID = "YwIJ2pDzUuQ";
const FEATURED_VIDEO_IDS = ["9XoX0v3HZOk", "nUDNe_rIZUY", "Ks389Zx8H3w"]; // Top Shelf — KeeZY · When I Catch You — Brittany Nicole · Better Than Me — Richie Blanco

export default function Home() {
  const [artists, setArtists] = useState([]);
  const [videos, setVideos] = useState([]);
  const popup = useVideoPopup();

  useEffect(() => {
    api.get("/artists").then((r) => setArtists(r.data));
    api.get("/videos").then((r) => setVideos(r.data));
  }, []);

  const openHero = () => popup?.open?.({ youtubeId: HERO_YOUTUBE_ID, title: "Pulse Media — 2024 Show Reel", eyebrow: "/ Now Playing" });

  return (
    <PageTransition>
      <Seo description="Music. Film. Technology. — A four-branch record label & media company pioneering new ways to experience entertainment." />
      {/* HERO */}
      <section className="relative pt-24 pb-20 overflow-hidden" data-testid="hero-section">
        {/* ambient bloom */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[1200px] h-[600px] rounded-full opacity-40"
               style={{ background: "radial-gradient(closest-side, rgba(0,184,255,0.30) 0%, transparent 70%)", filter: "blur(60px)" }} />
        </div>

        <div className="relative">
          {/* Sunken Hero Video — widescreen, edge-to-edge with thin black margins */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-6 md:mx-8 lg:mx-10"
          >
            <div className="sunken-bloom" />
          <div
            className="sunken-frame relative cursor-pointer group hero-video-frame"
            data-testid="hero-video-frame"
            onClick={openHero}
          >
            {/* On desktop: 20:9 frame with iframe scaled to 125% to crop YouTube chrome.
                On mobile: native 16:9 frame with iframe at 100% — nothing cropped, full clean video. */}
            <div className="absolute inset-0 overflow-hidden">
              <iframe
                title="Pulse Media — Hero"
                src={`https://www.youtube.com/embed/${HERO_YOUTUBE_ID}?autoplay=1&mute=1&controls=0&loop=1&playlist=${HERO_YOUTUBE_ID}&modestbranding=1&playsinline=1&rel=0&iv_load_policy=3&disablekb=1&showinfo=0`}
                className="hero-video-iframe absolute pointer-events-none"
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
                data-testid="hero-video"
              />
            </div>

              {/* corner indicator */}
              <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-md border border-white/10 z-10">
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#00B8FF" }} />
                <span className="label-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: "#fff" }}>LIVE · PORTAL NETWORK</span>
              </div>

              {/* hover play button */}
              <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-cyan-400/95 flex items-center justify-center shadow-[0_0_50px_rgba(0,184,255,0.7)]">
                  <Play size={32} fill="#000" stroke="#000" />
                </div>
              </div>

              {/* subtle gradient at bottom for depth */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none z-10" />
            </div>
          </motion.div>

          <div className="px-6 lg:px-10 max-w-7xl mx-auto mt-10 md:mt-0">

          {/* Animated logo + headline below the video — desktop only */}
          <motion.div
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="hidden md:flex justify-center mt-16 mb-8"
          >
            <LogoAnimation size={120} />
          </motion.div>

          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.7 }}
              className="label-mono mb-5"
            >
              MUSIC · FILM · TECHNOLOGY · STREAMING
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55, duration: 0.7 }}
              className="font-display text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter leading-[1.0]"
            >
              <span className="gradient-text">PULSE MEDIA</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-7 text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed"
            >
              The business and informational arm of Pulse Portal — a four-branch record label
              pioneering new ways to experience music, movies, and technology.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.7 }}
              className="mt-10 flex flex-wrap items-center justify-center gap-3"
            >
              <Link to="/artists" className="glass-btn solid" data-testid="hero-artists-btn">
                <Users size={14} /> Artists
              </Link>
              <Link to="/distribution" className="glass-btn" data-testid="hero-distro-btn">
                Distribution <ArrowUpRight size={14} />
              </Link>
            </motion.div>
          </div>
          </div>
        </div>
      </section>

      {/* PORTAL NETWORK */}
      <Section className="px-6 lg:px-10 py-24">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="label-mono mb-4">/ Portal Network</div>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter leading-[1.05]">
              Movies in 4K. TV in 4K.<br/>Exclusive interviews.<br/><span className="gradient-text">Early releases.</span>
            </h2>
            <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
              Pulse Portal is our streaming destination — built for the artists we sign and the stories we tell.
              One subscription unlocks the whole network.
            </p>
            <div className="mt-8 flex items-baseline gap-3">
              <span className="font-display text-7xl font-bold tracking-tighter">$1.99</span>
              <span className="label-mono">/ month</span>
            </div>
            <a href="http://www.pulseportal.tv" target="_blank" rel="noopener noreferrer" className="glass-btn solid mt-8" data-testid="portal-cta">
              Stream Now <ArrowUpRight size={14} />
            </a>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Music, label: "Music Videos", value: "100+ exclusives" },
              { icon: Film, label: "Film + TV Shows", value: "10+ originals" },
              { icon: VideoIcon, label: "Talk Shows", value: "Industry discussions" },
              { icon: Sparkles, label: "Exclusives", value: "Weekly drops" },
            ].map(({ icon: Icon, label, value }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="lift p-7 rounded-2xl border border-white/5 bg-[#121215]"
              >
                <Icon size={24} strokeWidth={1.5} className="text-cyan-400" />
                <div className="mt-6 label-mono">{label}</div>
                <div className="font-display text-2xl font-semibold tracking-tight">{value}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ARTISTS PREVIEW */}
      <Section className="px-6 lg:px-10 py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="label-mono mb-3">/ Roster</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">The artists.</h2>
            </div>
            <Link to="/artists" className="glass-btn ghost" data-testid="home-all-artists-btn">
              View all <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
            {artists.slice(0, 6).map((a, i) => (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
              >
                <Link to={`/${a.slug}`} className="block lift relative rounded-2xl overflow-hidden border border-white/5" data-testid={`home-artist-${a.slug}`}>
                  <div style={{ aspectRatio: "4/5" }} className="relative">
                    <Avatar src={a.image} name={a.name} className="absolute inset-0 w-full h-full" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <div className="label-mono text-cyan-300/90 mb-1">{a.tagline}</div>
                      <div className="font-display text-2xl font-bold tracking-tight">{a.name}</div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* VIDEO REEL */}
      <Section className="px-6 lg:px-10 py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="label-mono mb-3">/ The Lens</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tighter">From our reel.</h2>
            </div>
            <Link to="/videos" className="glass-btn ghost" data-testid="home-all-videos-btn">All videos <ArrowUpRight size={14} /></Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURED_VIDEO_IDS
              .map((yid) => videos.find((v) => v.youtube_id === yid))
              .filter(Boolean)
              .map((v) => (
                <YouTubeHoverCard key={v.id} youtubeId={v.youtube_id} title={v.title} category={v.artist} testid={`home-video-${v.id}`} />
              ))}
          </div>
        </div>
      </Section>

      {/* DISTRIBUTION CTA */}
      <Section className="px-6 lg:px-10 py-32 border-t border-white/5">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 mb-6"><Headphones size={16} className="text-cyan-400" /><div className="label-mono">/ Distribution</div></div>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.0]">
            We push your sound to <span className="gradient-text">230+ countries.</span>
          </h2>
          <p className="mt-8 text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            80+ DSPs. Monthly royalty collection. Powered by Intercept Music and Virgin Music Group, a division of Universal.
          </p>
          <div className="mt-10">
            <Link to="/distribution" className="glass-btn solid" data-testid="home-distro-cta">
              Submit for distribution <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </Section>
    </PageTransition>
  );
}
